const unPluralizeNames = ["shrinkwrapped", "unpacked or unpackaged"]

const renames: Record<string, string> = {
  "Ampoule, non-protected": "Ampoule",
  "Bale, compressed": "Bale",
  "Bottle, non-protected, cylindrical": "Bottle",
  "Container, not otherwise specified as transport equipment": "Container",
  "Tank container, generic": "Tank",
}

function pluralize() {
  const pluralRules: Array<[RegExp, string]> = []
  const singularRules: Array<[RegExp, string]> = []
  const uncountables: Record<string, boolean> = {}
  const irregularPlurals: Record<string, string> = {}
  const irregularSingles: Record<string, string> = {}

  function sanitizeRule(rule: string | RegExp): RegExp {
    if (typeof rule === "string") {
      return new RegExp("^" + rule + "$", "i")
    }
    return rule
  }

  function restoreCase(word: string, token: string): string {
    if (word === token) return token
    if (word === word.toLowerCase()) return token.toLowerCase()
    if (word === word.toUpperCase()) return token.toUpperCase()
    if (word[0] === word[0].toUpperCase()) {
      return token.charAt(0).toUpperCase() + token.substr(1).toLowerCase()
    }
    return token.toLowerCase()
  }

  function interpolate(str: string, args: string[]): string {
    return str.replace(/\$(\d{1,2})/g, (match, index) => {
      return args[parseInt(index)] || ""
    })
  }

  function replace(word: string, rule: [RegExp, string]): string {
    return word.replace(rule[0], (match: string, ...args: string[]) => {
      const result = interpolate(rule[1], args)
      if (match === "") {
        return restoreCase(word[parseInt(args[0]) - 1] || "", result)
      }
      return restoreCase(match, result)
    })
  }

  function sanitizeWord(
    token: string,
    word: string,
    rules: Array<[RegExp, string]>,
  ): string {
    if (
      !token.length ||
      Object.prototype.hasOwnProperty.call(uncountables, token)
    ) {
      return word
    }
    let len = rules.length
    while (len--) {
      const rule = rules[len]
      if (rule[0].test(word)) return replace(word, rule)
    }
    return word
  }

  function replaceWord(
    replaceMap: Record<string, string>,
    keepMap: Record<string, string>,
    rules: Array<[RegExp, string]>,
  ) {
    return (word: string): string => {
      const token = word.toLowerCase()
      if (Object.prototype.hasOwnProperty.call(keepMap, token)) {
        return restoreCase(word, token)
      }
      if (Object.prototype.hasOwnProperty.call(replaceMap, token)) {
        return restoreCase(word, replaceMap[token])
      }
      return sanitizeWord(token, word, rules)
    }
  }

  const pluralFunc = replaceWord(
    irregularSingles,
    irregularPlurals,
    pluralRules,
  )

  function addPluralRule(rule: string | RegExp, replacement: string) {
    pluralRules.push([sanitizeRule(rule), replacement])
  }

  function addSingularRule(rule: string | RegExp, replacement: string) {
    singularRules.push([sanitizeRule(rule), replacement])
  }

  function addUncountableRule(word: string | RegExp) {
    if (typeof word === "string") {
      uncountables[word.toLowerCase()] = true
      return
    }
    addPluralRule(word, "$0")
    addSingularRule(word, "$0")
  }

  function addIrregularRule(single: string, plural: string) {
    const pluralLower = plural.toLowerCase()
    const singleLower = single.toLowerCase()
    irregularSingles[singleLower] = pluralLower
    irregularPlurals[pluralLower] = singleLower
  }

  // Irregular rules
  [
    ["I", "we"],
    ["me", "us"],
    ["he", "they"],
    ["she", "they"],
    ["them", "them"],
    ["myself", "ourselves"],
    ["yourself", "yourselves"],
    ["itself", "themselves"],
    ["herself", "themselves"],
    ["himself", "themselves"],
    ["themself", "themselves"],
    ["is", "are"],
    ["was", "were"],
    ["has", "have"],
    ["this", "these"],
    ["that", "those"],
    ["echo", "echoes"],
    ["dingo", "dingoes"],
    ["volcano", "volcanoes"],
    ["tornado", "tornadoes"],
    ["torpedo", "torpedoes"],
    ["genus", "genera"],
    ["viscus", "viscera"],
    ["stigma", "stigmata"],
    ["stoma", "stomata"],
    ["dogma", "dogmata"],
    ["lemma", "lemmata"],
    ["schema", "schemata"],
    ["anathema", "anathemata"],
    ["ox", "oxen"],
    ["axe", "axes"],
    ["die", "dice"],
    ["yes", "yeses"],
    ["foot", "feet"],
    ["eave", "eaves"],
    ["goose", "geese"],
    ["tooth", "teeth"],
    ["quiz", "quizzes"],
    ["human", "humans"],
    ["proof", "proofs"],
    ["carve", "carves"],
    ["valve", "valves"],
    ["looey", "looies"],
    ["thief", "thieves"],
    ["groove", "grooves"],
    ["pickaxe", "pickaxes"],
    ["passerby", "passersby"],
  ].forEach((rule) => addIrregularRule(rule[0], rule[1]))

  // Pluralization rules
  const pluralRulesList: Array<[RegExp | string, string]> = [
    [/s?$/i, "s"],
    [/[^\p{ASCII}]$/u, "$0"],
    [/([^aeiou]ese)$/i, "$1"],
    [/(ax|test)is$/i, "$1es"],
    [/(alias|[^aou]us|t[lm]as|gas|ris)$/i, "$1es"],
    [/(e[mn]u)s?$/i, "$1s"],
    [/([^l]ias|[aeiou]las|[ejzr]as|[iu]am)$/i, "$1"],
    [
      /(alumn|syllab|vir|radi|nucle|fung|cact|stimul|termin|bacill|foc|uter|loc|strat)(?:us|i)$/i,
      "$1i",
    ],
    [/(alumn|alg|vertebr)(?:a|ae)$/i, "$1ae"],
    [/(seraph|cherub)(?:im)?$/i, "$1im"],
    [/(her|at|gr)o$/i, "$1oes"],
    [
      /(agend|addend|millenni|dat|extrem|bacteri|desiderat|strat|candelabr|errat|ov|symposi|curricul|automat|quor)(?:a|um)$/i,
      "$1a",
    ],
    [
      /(apheli|hyperbat|periheli|asyndet|noumen|phenomen|criteri|organ|prolegomen|hedr|automat)(?:a|on)$/i,
      "$1a",
    ],
    [/sis$/i, "ses"],
    [/(?:(kni|wi|li)fe|(ar|l|ea|eo|oa|hoo)f)$/i, "$1$2ves"],
    [/([^aeiouy]|qu)y$/i, "$1ies"],
    [/([^ch][ieo][ln])ey$/i, "$1ies"],
    [/(x|ch|ss|sh|zz)$/i, "$1es"],
    [/(matr|cod|mur|sil|vert|ind|append)(?:ix|ex)$/i, "$1ices"],
    [/\b((?:tit)?m|l)(?:ice|ouse)$/i, "$1ice"],
    [/(pe)(?:rson|ople)$/i, "$1ople"],
    [/(child)(?:ren)?$/i, "$1ren"],
    [/eaux$/i, "$0"],
    [/m[ae]n$/i, "men"],
    ["thou", "you"],
  ]
  pluralRulesList.forEach((rule) => addPluralRule(rule[0], rule[1]))

  // Uncountable rules
  const uncountableWords: Array<string | RegExp> = [
    "adulthood",
    "advice",
    "agenda",
    "aid",
    "aircraft",
    "alcohol",
    "ammo",
    "analytics",
    "anime",
    "athletics",
    "audio",
    "bison",
    "blood",
    "bream",
    "buffalo",
    "butter",
    "carp",
    "cash",
    "chassis",
    "chess",
    "clothing",
    "cod",
    "commerce",
    "cooperation",
    "corps",
    "debris",
    "diabetes",
    "digestion",
    "elk",
    "energy",
    "equipment",
    "excretion",
    "expertise",
    "firmware",
    "flounder",
    "fun",
    "gallows",
    "garbage",
    "graffiti",
    "hardware",
    "headquarters",
    "health",
    "herpes",
    "highjinks",
    "homework",
    "housework",
    "information",
    "jeans",
    "justice",
    "kudos",
    "labour",
    "literature",
    "machinery",
    "mackerel",
    "mail",
    "media",
    "mews",
    "moose",
    "music",
    "mud",
    "manga",
    "news",
    "only",
    "personnel",
    "pike",
    "plankton",
    "pliers",
    "police",
    "pollution",
    "premises",
    "rain",
    "research",
    "rice",
    "salmon",
    "scissors",
    "series",
    "sewage",
    "shambles",
    "shrimp",
    "software",
    "species",
    "staff",
    "swine",
    "tennis",
    "traffic",
    "transportation",
    "trout",
    "tuna",
    "wealth",
    "welfare",
    "whiting",
    "wildebeest",
    "wildlife",
    "you",
    /pok[eé]mon$/i,
    /[^aeiou]ese$/i,
    /deer$/i,
    /fish$/i,
    /measles$/i,
    /o[iu]s$/i,
    /pox$/i,
    /sheep$/i,
  ]
  uncountableWords.forEach((word) => addUncountableRule(word))

  return { plural: pluralFunc }
}

const plural = pluralize()

export const pluralizeName = (name: string) => {
  let trimName = name.trim()
  trimName = renames[trimName] ?? trimName

  const label = trimName
  let plural_label = trimName
  let restPart: string[] = []

  if (trimName.indexOf(",") >= 0) {
    const splits = trimName.split(",")
    restPart = splits.slice(1)
    plural_label =
      unPluralizeNames.indexOf(splits[0].toLowerCase()) >= 0
        ? splits[0]
        : plural.plural(splits[0])
  } else {
    plural_label =
      unPluralizeNames.indexOf(trimName.toLowerCase()) >= 0
        ? trimName
        : plural.plural(trimName)
  }

  plural_label = [plural_label, ...restPart].join(",")

  return {
    label,
    plural_label,
  }
}
